import React from 'react';
import { isEmpty } from 'ramda';
import { useApolloClient, useMutation } from '@apollo/client';
import { useHistory } from 'react-router';

import { userShape, bookingShape } from 'constants/propTypes';
import { codes, statusShortCode } from 'constants/payment';
import JourneyDigest from 'components/shared/JourneyDigest';
import { Page, Row } from 'components/shared/Layout';
import Payments from 'components/ui/Payments';
import { ER_OFFLINE_FEE, OFFLINE_FEE } from 'types/fees';
import buildJourneyDigestShape from 'utils/buildJourneyDigestShape';
import { CANCEL_PAYMENTS, ROLLBACK_VOUCHER_REDEMPTION } from 'store/mutations';
import { calcSegmentPointsTotal } from 'utils/functions';
import { hasHotel } from 'utils/booking';
import { isExtraOnly, nonCancelledExtras, totalExtras } from 'utils/payment';
import { mapVoucherCurrencyToMarket } from 'components/ui/Payments/Payments.utils';
import { GET_CUSTOMER_DETAILS } from 'store/queries';

import { getTotalAddonPrice } from '../JourneySearch/JourneySearch.utils';
import { bookingStatus } from 'types/booking';
import { isBookingMiceGroup } from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.utils';
import { getInfantPassengersFromDetails } from 'utils/getInfantPassengersFromDetails';

const PaymentBookingPath = ({ user: { canOverrideFees, paymentLocation }, bookingData }) => {
  const {
    fees,
    reference,
    currency,
    customer,
    passengers,
    outboundTotalPrice,
    outbound,
    inboundTotalPrice,
    inbound,
    totalPrice,
    status,
    totalPriceToBePaid,
    payments,
    amountDue,
    isPackage,
    isLoyalty,
    onHold,
    eurostarOutbound,
    eurostarInbound,
    hotels,
    productFamilies,
    outboundOrigin,
    outboundDestination,
    outboundExtras,
    inboundExtras,
    isInAfterSales,
    additionalDetails
  } = bookingData;

  const client = useApolloClient();
  const { location } = useHistory();

  const isBookingMice = isBookingMiceGroup({ details: additionalDetails });
  const infantCount = getInfantPassengersFromDetails({ additionalDetails }).length;
  const membershipNumber = new URLSearchParams(location?.search).get('membershipNumber');

  const [cancelPaymentsMutation, { loading: cancelPaymentsLoading }] = useMutation(CANCEL_PAYMENTS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [rollbackVoucherRedemption, { loading: rollbackVoucherRedemptionLoading }] = useMutation(ROLLBACK_VOUCHER_REDEMPTION);

  const cancelPayments = async () => {
    let customerInfo;

    if (membershipNumber) {
      try {
        const result = await client.query({
          query: GET_CUSTOMER_DETAILS,
          variables: {
            membershipNumber
          }
        });

        customerInfo = result?.data?.getCustomerDetails;
      } catch (e) {
        console.error('Failed to get customer details:', e);
      }
    }

    const tasks = [
      cancelPaymentsMutation({
        variables: {
          bookingReference: reference,
          paymentLocation: paymentLocation.name,
          currency,
          hasHotel: isPackage || hasHotel(bookingData),
          customerId: customerInfo?.profile?.customerId,
          shouldCancelLoyalty: true
        }
      })
    ];
    const voucherPayments = payments.filter(
      ({ method, paymentStatus, amount }) => method === codes.VOUCHER && paymentStatus === statusShortCode.pending && amount !== 0
    );
    if (voucherPayments.length) {
      tasks.push(
        ...voucherPayments.map((payment) =>
          rollbackVoucherRedemption({
            variables: {
              voucherCodeLastChars: payment.reference,
              market: mapVoucherCurrencyToMarket[currency],
              bookingReference: reference
            }
          })
        )
      );
    }

    return Promise.all(tasks);
  };

  const outboundTotalPoints = calcSegmentPointsTotal(eurostarOutbound);
  const inboundTotalPoints = calcSegmentPointsTotal(eurostarInbound);

  const offlineFees = fees.filter(({ code, cancelled }) => (code === OFFLINE_FEE || code === ER_OFFLINE_FEE) && !cancelled);
  const firstOfflineFee = offlineFees.length > 0 ? offlineFees[0] : null;

  const isReturn = !isEmpty(outbound) && !isEmpty(inbound);

  const { outboundTotalAddOnPrice = 0, inboundTotalAddOnPrice = 0 } = getTotalAddonPrice({
    outbound,
    inbound
  });

  const nonCancelledOutboundExtras = nonCancelledExtras(outboundExtras);
  const nonCancelledInboundExtras = nonCancelledExtras(inboundExtras);
  const outboundTotalExtrasPrice = totalExtras(nonCancelledOutboundExtras);
  const inboundTotalExtrasPrice = totalExtras(nonCancelledInboundExtras);

  const isExtrasBooking = isExtraOnly(outbound, inbound, outboundExtras, inboundExtras);

  return (
    <>
      <Page>
        <Row span={16}>
          <Row start={2} span={14}>
            <Payments
              reference={reference}
              customer={customer}
              currency={currency}
              status={status}
              totalPriceToBePaid={totalPriceToBePaid}
              payments={payments}
              isPackage={isPackage}
              isLoyalty={isLoyalty}
              hasHotel={hasHotel({ hotels })}
              amountDue={amountDue}
              depositAmount={onHold.depositAmount}
              cancelPaymentsLoading={cancelPaymentsLoading || rollbackVoucherRedemptionLoading}
              cancelPayments={cancelPayments}
              outbound={outbound}
              inbound={inbound}
              passengers={passengers}
              productFamilies={productFamilies}
              isExtrasOnly={isExtrasBooking}
              isInAfterSales={isInAfterSales || [bookingStatus.PENDING_CHANGES].includes(status)}
            />
          </Row>
        </Row>
      </Page>

      <JourneyDigest
        outbound={buildJourneyDigestShape(outbound)}
        inbound={buildJourneyDigestShape(inbound)}
        outboundTotalPrice={outboundTotalPrice + outboundTotalAddOnPrice + outboundTotalExtrasPrice}
        outboundTotalPoints={outboundTotalPoints}
        inboundTotalPrice={inboundTotalPrice + inboundTotalAddOnPrice + inboundTotalExtrasPrice}
        inboundTotalPoints={inboundTotalPoints}
        totalPrice={totalPrice}
        isReturnJourney={isReturn}
        passengers={passengers}
        currency={currency}
        offlineFee={firstOfflineFee}
        originUIC={outboundOrigin?.uic}
        destinationUIC={outboundDestination?.uic}
        outboundExtras={nonCancelledOutboundExtras}
        inboundExtras={nonCancelledInboundExtras}
        isBookingMice={isBookingMice}
        infantCount={infantCount}
        isLoyaltyBooking={isLoyalty}
      />
    </>
  );
};

PaymentBookingPath.propTypes = {
  bookingData: bookingShape,
  user: userShape
};

export default PaymentBookingPath;
